import CheckBox from "@/components/checkBox/checkBox.vue";
import MonthCalendar from "@/components/monthCalendar/monthCalendar.vue";
import ServiceTimeGuestCounts from "@/components/ServiceTimeGuestCounts/ServiceTimeGuestCounts.vue";
import moment from "moment";
import { createNamespacedHelpers } from "vuex";
import { swalDiscardChangesConfig } from "@/swalModals";
import { GUEST_COUNT_STRATEGY } from "@/enums";
import { SettingsService } from "@/services/settingsApi";

const { mapGetters: userGetters } = createNamespacedHelpers("user"),
	MAX_ST = 5;

export default {
	components: {
		CheckBox,
		MonthCalendar,
		ServiceTimeGuestCounts,
	},

	data() {
		return {
			isSaving: false, // To track the saving state of `guest-count_button_save`
			showDaysWithDefaultValue: false,
			activeDate: null,
			// @type {string} used to determine if the this.data changed
			initialData: "",
			daysWithDailyValues: [],

			// all guest data, will be modified when user types in the inputs in the UI
			data: [],
			serviceTimeSets: {},

			guestCounts: [],

			lastEdited: null,

			GUEST_COUNT_STRATEGY,
		};
	},

	computed: {
		...userGetters(["activeOutlet"]),
		dataChanged,
		dailyData,
		filteredGuestCounts,
	},

	created() {
		this.initialize();
	},

	async beforeRouteLeave(to, from, next) {
		// if the data hasn't been edited proceed next
		if (!this.dataChanged) {
			next();
			return;
		}

		// but if it has been changed show modal to discard the changes
		const answer = await this.$swal({ ...swalDiscardChangesConfig });

		if (answer.value) next();
	},

	methods: {
		initialize,
		onDateChange,
		generateGuestCount,
		fetchData,
		setActiveDate,
		onToggleDefaultValues,
		saveReferenceForDeterminingIfDataChanged,
		save,
		isBeforeMeasurementStarted,
		updateGCArray,
	},
};

function dataChanged() {
	if (!this.initialData) return false;

	return this.initialData !== JSON.stringify(this.dailyData);
}

function initialize() {
	/*
	 * if we are navigated here from the Waste per guest table, we want to show the guest
	 * counts for the selected month, which is stored in the sessionStorage
	 */

	if ("guestCountMonth" in sessionStorage) {
		const date = moment(sessionStorage.guestCountMonth, "YYYY/MM").format("YYYY-MM-DD");
		this.setActiveDate(date);
		sessionStorage.removeItem("guestCountMonth");
	} else this.setActiveDate(this.activeOutlet.outlet_data_validity);
}

function setActiveDate(date) {
	this.activeDate = date;
	this.fetchData();
}

function onToggleDefaultValues(value) {
	this.showDaysWithDefaultValue = value;
}

async function onDateChange(date) {
	if (this.dataChanged) {
		// but if it has been changed show modal to discard the changes
		const answer = await this.$swal({ ...swalDiscardChangesConfig });

		if (answer.value) this.setActiveDate(date);
	} else this.setActiveDate(date);
}

async function fetchData() {
	const start = moment(this.activeDate).startOf("month"),
		end = moment(this.activeDate).endOf("month").add(1, "day");
	try {
		const { data } = await SettingsService.getGuestCount(start, end);
		this.serviceTimeSets = data.service_time_sets;

		this.lastEdited = data.last_edited;

		this.data = this.generateGuestCount(data.guest_counts);

		this.saveReferenceForDeterminingIfDataChanged();

		this.daysWithDailyValues = data.guest_counts.map((gcObj) => gcObj.date);
	} catch (error) {
		// ignore 404 responses
		return {}; // todo
	}
}

function generateGuestCount(dailyGuestCounts) {
	const startDate = moment(this.activeDate).startOf("month"),
		endDate = moment(this.activeDate).endOf("month").add(1, "day"),
		diff = endDate.diff(startDate, "days"),
		daysInMonth = [];
	for (let index = 0; index < diff; index++) daysInMonth.push(moment(startDate).add(index, "day").format("YYYY-MM-DD"));

	const allGuestCount = daysInMonth.map((date) => {
		const GCObj = {
				date,
				isClosed: this.$utils.isClosed(date),
				isBeforeMeasStarted: this.isBeforeMeasurementStarted(date),
			},
			dailyValue = dailyGuestCounts.find((gcObj) => gcObj.date === date);

		if (dailyValue) GCObj.dailyGC = dailyValue.gc;
		else GCObj.dailyGC = Array(MAX_ST + 1).fill(null);

		return GCObj;
	});

	return allGuestCount;
}

/*
 * If the "show days with default value" filter is active,
 * filter out the days that already had daily values saved
 */
function filteredGuestCounts() {
	if (!this.showDaysWithDefaultValue) return this.data;

	return this.data.filter((gcObj) => !this.daysWithDailyValues.includes(gcObj.date));
}

function saveReferenceForDeterminingIfDataChanged() {
	this.initialData = JSON.stringify(this.dailyData);
}

function dailyData() {
	return this.data.map((GCObj) => ({
		gc: GCObj.dailyGC,
		date: moment(GCObj.date).format("YYYY-MM-DD"),
	}));
}

async function save() {
	if (this.isSaving) return; // Prevent the function from running if already saving
	this.isSaving = true; // Disable the button if first keypress

	try {
		await SettingsService.setGuestCount(this.dailyData);

		this.$ma.trackEvent({
			action: "Guest count added",
			properties: {
				Value: this.dailyData,
				Component: this.$route.name,
			},
		});
		this.$swal({
			text: this.$t("guest_count.success"),
			customClass: "swal_modal",
		});

		await this.fetchData();
	} catch (error) {
		this.$swal({
			text: error.message,
			customClass: "swal_modal",
		});
	} finally {
		this.isSaving = false; // Enable request again after request is done/fails
	}
}

function isBeforeMeasurementStarted(date) {
	try {
		if (date < this.activeOutlet.outlet_start_date) return true;

		return false;
	} catch (error) {
		//if no start_date is precised, then always return false
		return false;
	}
}

function updateGCArray(day, index, value) {
	// find day in this.data, update its dailyGC to have the given value in the given index
	const dayIndexInData = this.data.indexOf(this.data.find((gcObj) => gcObj.date === day));

	this.$set(this.data[dayIndexInData].dailyGC, index, value);
}
